import React from 'react';
import { Link } from 'react-router-dom';

import { usePaperDocument } from '../hooks/usePaperDocument';
import './paper.css';

const REPO = 'https://github.com/TheoTime01/ChatMoov';
const VIDEO =
  'https://www.youtube.com/playlist?list=PLxonWIbIdwC61nbJOSWWsmZ7bKpfgkA30';
const EMBED =
  'https://www.youtube-nocookie.com/embed/videoseries?list=PLxonWIbIdwC61nbJOSWWsmZ7bKpfgkA30';
const INMOOV = 'https://inmoov.fr/head-3/';

/* Display equations are written as MathML so that they render natively in the
 * document font instead of pulling in a formula-typesetting library. React
 * renders MathML tags, but going through innerHTML keeps the markup verbatim
 * and keeps this file readable. */
const Equation = ({ tag, math }) => (
  <div className="eq">
    <div className="eq-body" dangerouslySetInnerHTML={{ __html: math }} />
    <div className="eq-tag">({tag})</div>
  </div>
);

const EQ_ERROR = `
<math display="block">
  <msub><mi>u</mi><mi>f</mi></msub><mo>=</mo>
  <mi>x</mi><mo>+</mo><mfrac><mi>w</mi><mn>2</mn></mfrac>
  <mo>,</mo><mspace width="1.5em"/>
  <msub><mi>e</mi><mi>x</mi></msub><mo>=</mo>
  <mn>30</mn>
  <mfrac>
    <mrow>
      <msub><mi>u</mi><mi>f</mi></msub><mo>&#x2212;</mo>
      <mi>W</mi><mo>/</mo><mn>2</mn>
    </mrow>
    <mrow><mi>W</mi><mo>/</mo><mn>2</mn></mrow>
  </mfrac>
  <mo>&#x2208;</mo>
  <mrow><mo stretchy="false">[</mo><mo>&#x2212;</mo><mn>30</mn>
  <mo>,</mo><mspace width="0.3em"/><mn>30</mn><mo stretchy="false">]</mo></mrow>
</math>`;

const EQ_CONTROL = `
<math display="block">
  <mtable columnalign="left">
    <mtr><mtd>
      <mtext>target</mtext><mo>=</mo>
      <mn>180</mn><mo>&#xB0;</mo><mo>&#x22C5;</mo>
      <mi>sgn</mi><mo>&#x2061;</mo>
      <mrow><mo stretchy="false">(</mo><msub><mi>e</mi><mi>x</mi></msub><mo stretchy="false">)</mo></mrow>
      <mo>,</mo><mspace width="0.6em"/>
      <mtext>speed</mtext><mo>=</mo>
      <mrow><mo>|</mo><msub><mi>e</mi><mi>x</mi></msub><mo>|</mo></mrow>
      <mspace width="1.6em"/>
      <mtext>if&#xA0;</mtext>
      <mrow><mo>|</mo><msub><mi>e</mi><mi>x</mi></msub><mo>|</mo></mrow>
      <mo>&gt;</mo><mn>2</mn>
    </mtd></mtr>
    <mtr><mtd>
      <mtext>hold at current position</mtext>
      <mspace width="1.6em"/>
      <mtext>otherwise</mtext>
    </mtd></mtr>
  </mtable>
</math>`;

const EQ_DEADBAND = `
<math display="block">
  <mi>&#x394;</mi><mi>u</mi><mo>=</mo>
  <mfrac><mn>2</mn><mn>30</mn></mfrac>
  <mo>&#x22C5;</mo>
  <mfrac><mi>W</mi><mn>2</mn></mfrac>
  <mo>=</mo>
  <mfrac><mn>640</mn><mn>30</mn></mfrac>
  <mo>&#x2248;</mo>
  <mn>21</mn><mspace width="0.2em"/><mtext>px</mtext>
  <mspace width="1em"/>
  <mrow><mo stretchy="false">(</mo><mo>&#xB1;</mo><mn>3.3</mn><mo>%</mo>
  <mtext>&#xA0;of frame width</mtext><mo stretchy="false">)</mo></mrow>
</math>`;

const EQ_RECENTRE = `
<math display="block">
  <msub><mi>t</mi><mtext>recentre</mtext></msub><mo>=</mo>
  <mfrac>
    <msub><mi>N</mi><mtext>miss</mtext></msub>
    <mrow><msub><mi>f</mi><mtext>cam</mtext></msub><mo>/</mo><mn>2</mn></mrow>
  </mfrac>
  <mo>=</mo>
  <mfrac><mn>100</mn><mn>15</mn></mfrac>
  <mo>&#x2248;</mo>
  <mn>6.7</mn><mspace width="0.2em"/><mi>s</mi>
</math>`;

const EQ_CHUNKS = `
<math display="block">
  <msub><mi>N</mi><mtext>chunks</mtext></msub><mo>=</mo>
  <mfrac>
    <mrow>
      <msub><mi>f</mi><mi>s</mi></msub><msub><mi>T</mi><mtext>max</mtext></msub>
    </mrow>
    <mi>C</mi>
  </mfrac>
  <mo>=</mo>
  <mfrac>
    <mrow><mn>44100</mn><mo>&#x22C5;</mo><mn>6</mn></mrow>
    <mn>1024</mn>
  </mfrac>
  <mo>&#x2248;</mo><mn>259</mn>
</math>`;

const EQ_ENVELOPE = `
<math display="block">
  <mi>a</mi><mo stretchy="false">[</mo><mi>n</mi><mo stretchy="false">]</mo>
  <mo>=</mo>
  <mn>130</mn>
  <msqrt>
    <msup>
      <mrow><mover><mi>s</mi><mo>&#x7E;</mo></mover>
      <mo stretchy="false">[</mo><mi>n</mi><mo stretchy="false">]</mo></mrow>
      <mn>2</mn>
    </msup>
  </msqrt>
  <mo>+</mo><mn>50</mn>
  <mo>&#x2261;</mo>
  <mn>130</mn>
  <mrow><mo>|</mo><mover><mi>s</mi><mo>&#x7E;</mo></mover>
  <mo stretchy="false">[</mo><mi>n</mi><mo stretchy="false">]</mo><mo>|</mo></mrow>
  <mo>+</mo><mn>50</mn>
  <mo>&#x2208;</mo>
  <mrow><mo stretchy="false">[</mo><mn>50</mn><mo>&#xB0;</mo>
  <mo>,</mo><mspace width="0.3em"/><mn>180</mn><mo>&#xB0;</mo><mo stretchy="false">]</mo></mrow>
</math>`;

const EQ_RATE = `
<math display="block">
  <mi>R</mi><mo>=</mo>
  <mi>b</mi><mo>&#x22C5;</mo><msub><mi>f</mi><mi>s</mi></msub>
  <mo>=</mo>
  <mn>19</mn><mo>&#x22C5;</mo><mn>22050</mn>
  <mo>&#x2248;</mo>
  <mn>4.2</mn><mo>&#xD7;</mo>
  <msup><mn>10</mn><mn>5</mn></msup>
  <mspace width="0.2em"/><mtext>B/s</mtext>
</math>`;

const EQ_SLOWDOWN = `
<math display="block">
  <mi>k</mi><mo>=</mo>
  <mfrac>
    <mi>R</mi>
    <mrow><msub><mi>B</mi><mtext>baud</mtext></msub><mo>/</mo><mn>10</mn></mrow>
  </mfrac>
  <mo>=</mo>
  <mfrac><mrow><mn>4.2</mn><mo>&#xD7;</mo><msup><mn>10</mn><mn>5</mn></msup></mrow><mn>960</mn></mfrac>
  <mo>&#x2248;</mo><mn>436</mn>
  <mspace width="1.5em"/>
  <mrow><mo stretchy="false">(</mo>
  <mn>33</mn><mtext>&#xA0;at 128000&#xA0;baud</mtext>
  <mo stretchy="false">)</mo></mrow>
</math>`;

const EQ_DECIMATE = `
<math display="block">
  <msup><mi>R</mi><mo>&#x2032;</mo></msup><mo>=</mo>
  <mn>1</mn><mspace width="0.2em"/><mtext>B</mtext>
  <mo>&#x22C5;</mo><mn>50</mn><mspace width="0.2em"/><mtext>Hz</mtext>
  <mo>=</mo><mn>50</mn><mspace width="0.2em"/><mtext>B/s</mtext>
  <mspace width="1.5em"/>
  <mrow><mo stretchy="false">(</mo>
  <mn>5</mn><mo>%</mo><mtext>&#xA0;of a 9600-baud link;&#xA0;</mtext>
  <mn>8400</mn><mo>&#xD7;</mo><mtext>&#xA0;reduction</mtext>
  <mo stretchy="false">)</mo></mrow>
</math>`;

const ChatMoovProject = () => {
  const { theme, toggleTheme } = usePaperDocument('/projects/chatmoov');

  return (
    <article className="paper-doc">
      <div className="doc-bar">
        <Link to="/">&larr; Théotime Perrichet</Link>
        <button type="button" onClick={toggleTheme}>
          {theme === 'dark' ? ': Light :' : ': Dark :'}
        </button>
      </div>

      <h1>
        ChatMoov: face tracking and spoken human–machine interaction on the head
        of an INMOOV robot
      </h1>

      <p className="author">Théotime Perrichet</p>
      <p className="doc-affiliation">
        4th-year project, Service Robotics major, CPE Lyon
      </p>
      <p className="doc-dateline">Lyon, France · 2023</p>

      <ul className="doc-links">
        <li><a href={REPO}>Source code</a></li>
        <li><a href={VIDEO}>Pitch &amp; tutorial videos</a></li>
        <li><a href={INMOOV}>INMOOV head v3</a></li>
      </ul>

      <div className="abstract">
        <h2>Abstract</h2>
        <p>
          This report documents ChatMoov, a system that turns the head of an
          INMOOV <a href="#r1">[1]</a> 3D-printed humanoid into a conversational
          agent. Two subsystems run as independent OS processes: a vision loop
          that detects a face in a webcam stream and pans the neck to keep it
          centred, and an interaction loop that records speech, transcribes it
          with Whisper, answers with a GPT chat model, synthesises the reply and
          drives the jaw servo from the audio envelope. The write-up
          reconstructs the control law the firmware actually implements. That law
          is not a position controller but proportional control in the{' '}
          <em>velocity</em> domain, obtained by always commanding an end stop and
          feeding the pixel error in as the slew rate. The write-up derives its
          &plusmn;21&nbsp;px dead band and its 6.7&nbsp;s target-loss timeout.
          It then gives a bandwidth budget for the jaw link showing that the
          committed transmitter over-subscribes its serial line by a factor of
          436, explains why the firmware answers that with a one-bit protocol,
          and derives the decimation that would remove the constraint. A
          source-level review closes the report with fourteen findings, among
          them a units mismatch that disables the voice-activity gate and a baud
          rate that disagrees between host and firmware on both serial links.
        </p>
        <p className="keywords">
          <strong>Keywords:</strong> humanoid robotics · face tracking · Haar
          cascades · servo control · speech interfaces · large language models ·
          serial protocols · embedded systems
        </p>
      </div>

      <nav aria-label="Table of contents">
        <ol>
          <li><a href="#s1">Introduction</a></li>
          <li><a href="#s2">Platform and architecture</a></li>
          <li><a href="#s3">Perception: face tracking</a></li>
          <li><a href="#s4">Interaction: the conversational loop</a></li>
          <li><a href="#s5">Jaw actuation from the audio envelope</a></li>
          <li><a href="#s6">Host&ndash;microcontroller protocol</a></li>
          <li><a href="#s7">Demonstration</a></li>
          <li><a href="#s8">Source-level review</a></li>
          <li><a href="#s9">What the design gets right</a></li>
          <li><a href="#s10">Reproducing the system</a></li>
        </ol>
      </nav>

      <h2 id="s1">1. Introduction</h2>
      <p>
        The INMOOV head <a href="#r1">[1]</a> is an open-source 3D-printed
        assembly whose neck and jaw are driven by hobby servos. On its own it is
        a mechanism with no behaviour. The objective set for this project was to
        give it one: to make it look at whoever is speaking to it and answer them
        out loud, using a large language model as the conversational core rather
        than a hand-written dialogue tree.
      </p>
      <p>
        The engineering interest is not in any one of those capabilities. Face
        detection with a Haar cascade <a href="#r2">[2]</a> is a solved textbook
        exercise, and calling a chat completion endpoint is three lines. The
        interest is in what happens where they meet: a perception loop that must
        run at camera rate, a dialogue loop whose latency is set by a network
        round-trip it does not control, two actuators that accept nothing but
        positions, and two narrow serial links carrying every command between
        them. Three constraints follow, and they shape every decision in the rest
        of this report.
      </p>
      <p>
        <strong>The actuators are dumb.</strong> A hobby servo accepts a
        position, not a torque or a velocity. Any control law has to be expressed
        as a sequence of position commands, and the only tunable dynamic quantity
        is the slew rate offered by the <code>VarSpeedServo</code> library{' '}
        <a href="#r3">[3]</a>. §3.3 shows what falls out of that.
      </p>
      <p>
        <strong>The host is a laptop.</strong> Running speech recognition and a
        language model locally was out of budget for the hardware available, so
        transcription, dialogue and synthesis are all offloaded to network
        services <a href="#r4">[4]</a>,&nbsp;<a href="#r5">[5]</a>. This buys capability
        and costs latency, a trade §4.5 quantifies and the architecture has to
        absorb rather than hide.
      </p>
      <p>
        <strong>Serial links are narrow.</strong> Everything the host wants the
        head to do passes through a UART as ASCII. §5.2 shows this constraint was
        under-estimated by more than two orders of magnitude on the jaw channel,
        and §6 gives the protocol the firmware settled on as a result.
      </p>

      <h2 id="s2">2. Platform and architecture</h2>

      <h3>2.1 Process decomposition</h3>
      <p>
        The system splits into two top-level processes, launched by{' '}
        <code>inmoov_v1.py</code>, which installs a <code>SIGINT</code> handler
        so that a single Ctrl-C terminates both children before it terminates the
        parent.
      </p>

      <figure>
        <div className="fig-plate">
          <img
            src="/images/chatmoov/processus.png"
            alt="Process graph: a root node branching to P0 and P1; P1 leading to C0, then C1, C2 and C3, with C3 looping back to C0"
          />
        </div>
        <figcaption>
          Process decomposition. <strong>P0</strong> is the vision loop.{' '}
          <strong>P1</strong> is the interaction loop, itself a cycle over four
          stages: <strong>C0</strong> capture, <strong>C1</strong> transcription,{' '}
          <strong>C2</strong> dialogue, <strong>C3</strong> synthesis and
          playback, closing back onto C0 for the next turn.
        </figcaption>
      </figure>

      <p>
        The split is a direct answer to the hardest constraint in any interactive
        robot: perception must not stall behind network I/O. P0 runs an
        uninterrupted OpenCV loop at camera rate; P1 spends most of its
        wall-clock time blocked on a socket. Putting them in separate OS
        processes means P1&rsquo;s blocking can never starve P0. The cost is that
        they share no state: the head does not, for instance, stop tracking
        while it is speaking, and nothing tells the vision loop that the person
        it is looking at is the one currently talking.
      </p>

      <h3>2.2 Control flow</h3>

      <figure>
        <div className="fig-wide">
          <img
            src="/images/chatmoov/diagram.png"
            alt="Flowchart of both processes: camera check and face tracking loop on the left; audio capture, Whisper transcription, chat completion, text-to-speech and playback on the right, with error branches raising ValueError"
          />
        </div>
        <figcaption>
          Full control-flow diagram, as committed with the project. Cloud-served
          stages carry a network glyph; every stage has an explicit failure edge
          that raises <code>ValueError</code> and terminates its branch. The
          figure is wider than the measure, so scroll it sideways, or open it{' '}
          <a href="/images/chatmoov/diagram.png">at full size</a>.
        </figcaption>
      </figure>

      <p>
        Two properties of this diagram are worth naming because they are
        deliberate rather than incidental. First, every externally-served stage
        (C1, C2, C3) is guarded by a <code>!= None</code> test before its output
        is passed downstream, so a failed API call surfaces as an exception at
        the stage that caused it rather than as a null propagating into a servo
        command. Second, the loop closes from C3 back to C0, not to P1: a
        completed turn re-arms the microphone directly, without re-running the
        start-up checks.
      </p>

      <h3>2.3 Bill of materials</h3>

      <div className="scroll-wrapper">
        <table>
          <caption>Hardware and services required to reproduce the build.</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Role</th>
              <th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>INMOOV head (v3)</td>
              <td>Mechanical platform</td>
              <td>3D-printed; neck yaw and jaw are the two driven axes</td>
            </tr>
            <tr>
              <td>USB camera</td>
              <td>P0 sensor</td>
              <td>Opened as device <code>0</code>, forced to 640&nbsp;&times;&nbsp;480</td>
            </tr>
            <tr>
              <td>Microphone</td>
              <td>C0 sensor</td>
              <td>Default PyAudio input; mono, 44.1&nbsp;kHz, 16-bit</td>
            </tr>
            <tr>
              <td>Arduino&nbsp;#1</td>
              <td>Neck yaw servo</td>
              <td>Runs <code>face_detector.ino</code>, servo on pin&nbsp;9</td>
            </tr>
            <tr>
              <td>Arduino&nbsp;#2</td>
              <td>Jaw servo</td>
              <td>Runs <code>inmoov_jaw.ino</code>, servo on pin&nbsp;9</td>
            </tr>
            <tr>
              <td>Speaker</td>
              <td>C3 output</td>
              <td>Driven by the host, not by either Arduino</td>
            </tr>
            <tr>
              <td>OpenAI account</td>
              <td>C1 + C2</td>
              <td>Whisper <a href="#r4">[4]</a> for transcription, a GPT chat model <a href="#r5">[5]</a> for dialogue</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Two microcontrollers for two servos is more silicon than the axis count
        demands, and §9 lists collapsing them as planned work. It is a defensible
        simplification for a first build: each board owns one axis, one baud rate
        and one framing convention, so the two motion behaviours could be
        developed and flashed independently without either one&rsquo;s serial
        traffic interfering with the other&rsquo;s timing.
      </p>

      <h3>2.4 Module map</h3>

      <div className="scroll-wrapper">
        <table>
          <caption>Source tree, by responsibility.</caption>
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Stage</th>
              <th scope="col">Responsibility</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>inmoov_v1.py</code></td><td>n/a</td>
              <td>Launcher; spawns P0 and P1, handles <code>SIGINT</code></td>
            </tr>
            <tr>
              <td><code>face_detector.py</code></td><td>P0</td>
              <td><code>FaceDetector</code>: capture, Haar detection, error signal, serial write</td>
            </tr>
            <tr>
              <td><code>face_detector.ino</code></td><td>P0</td>
              <td>Neck firmware: frame parsing, dead band, re-centring</td>
            </tr>
            <tr>
              <td><code>conversation.py</code></td><td>P1</td>
              <td>Turn scheduler; runs C0&ndash;C1 then C2&ndash;C3 as sequential subprocesses</td>
            </tr>
            <tr>
              <td><code>speech_to_text.py</code></td><td>C0, C1</td>
              <td><code>Recording</code> (gated capture) and <code>Speech_To_Text</code> (Whisper)</td>
            </tr>
            <tr>
              <td><code>text_to_speech_chatgpt.py</code></td><td>C2, C3</td>
              <td>Chat completion, gTTS synthesis, playback</td>
            </tr>
            <tr>
              <td><code>AudioSerialAnalyzer.py</code></td><td>C3</td>
              <td>Audio envelope &rarr; jaw servo angle stream</td>
            </tr>
            <tr>
              <td><code>inmoov_jaw.ino</code></td><td>C3</td>
              <td>Jaw firmware: open/close oscillation driven by a binary flag</td>
            </tr>
            <tr>
              <td><code>API/api.py</code></td><td>n/a</td>
              <td><code>GPT_API</code>: loads key and organisation from disk</td>
            </tr>
            <tr>
              <td><code>haarcascade_*.xml</code></td><td>P0</td>
              <td>Pre-trained frontal-face and profile-face classifiers</td>
            </tr>
            <tr>
              <td><code>Docker/</code></td><td>n/a</td>
              <td>whisper.cpp <a href="#r6">[6]</a> image; groundwork for on-device transcription</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="s3">3. Perception: face tracking</h2>

      <h3>3.1 Detection</h3>
      <p>
        P0 opens the camera, forces 640&nbsp;&times;&nbsp;480, mirrors each frame
        with <code>cv2.flip</code> so that the sign of the error matches an
        operator&rsquo;s intuition when watching the preview window, and runs a
        Viola&ndash;Jones cascade <a href="#r2">[2]</a> on the greyscale image:
      </p>

      <pre><code>{`faces = self.face_cascade.detectMultiScale(
    gray,
    scaleFactor=1.1,
    minNeighbors=20,
    minSize=(30, 30)
)`}</code></pre>

      <p>
        The parameters trade recall for precision, and for this application that
        is the right side to err on. <code>scaleFactor=1.1</code> gives a fine
        image pyramid: more scales searched, more compute per frame, better
        detection of faces at intermediate distances.{' '}
        <code>minNeighbors=20</code> is aggressive, since a candidate window must
        survive twenty overlapping detections to be reported. A false positive
        here does not produce a wrong bounding box, it produces{' '}
        <em>motion</em>: the head physically snaps toward a texture on the wall.
        A missed detection costs nothing, because the next frame arrives in tens
        of milliseconds and §3.4 shows the firmware holds position through short
        dropouts. Precision is worth far more than recall when the output is
        torque.
      </p>
      <p>
        Only <code>faces[0]</code> is ever used. With more than one person in
        shot the head tracks whichever face OpenCV happens to return first, which
        is not a stable identity across frames; associating each detection with
        the nearest previous target would be the minimal fix
        (<a href="#f-13">F13</a>).
      </p>

      <h3>3.2 Error signal</h3>
      <p>
        Let the detected bounding box be{' '}
        <math><mrow><mo stretchy="false">(</mo><mi>x</mi><mo>,</mo><mi>y</mi><mo>,</mo><mi>w</mi><mo>,</mo><mi>h</mi><mo stretchy="false">)</mo></mrow></math>{' '}
        in pixels and the frame{' '}
        <math><mrow><mi>W</mi><mo>&#xD7;</mo><mi>H</mi></mrow></math>. The face
        centre and the normalised horizontal error are
      </p>

      <Equation tag="1" math={EQ_ERROR} />

      <p>
        The normalisation matters more than it first appears. It puts the error
        on a fixed &plusmn;30 scale regardless of frame width, and the same
        number is then reinterpreted downstream as a servo <em>speed</em>, so the
        constant 30 is simultaneously a normalisation gain and the maximum slew
        rate the neck will ever be commanded at. Changing the camera resolution
        leaves the control behaviour untouched; changing the constant 30 changes
        both the sensitivity and the top speed together.
      </p>
      <p>
        A vertical error is computed by the same formula and then never
        transmitted. The committed system is single-axis: yaw only. The pitch
        term is in place for a second servo that was not wired up
        (<a href="#f-13">F13</a>); the protocol of §6 already has room for it.
      </p>

      <h3>3.3 Control law</h3>
      <p>
        The interesting decision is on the firmware side. The host sends the
        error; the Arduino turns it into motion:
      </p>

      <pre><code>{`if (vel > 2)
  servo1.write(180, vel, false);
else if (vel < -2)
  servo1.write(0, -vel, false);
else {
  pos = servo1.read();
  servo1.write(pos, 255, false);
}`}</code></pre>

      <p>
        This is not a position controller. The <em>target</em> is always an end
        stop, 180&deg; when the face is to one side and 0&deg; when it is to the
        other, and the error magnitude is fed in as the <em>slew rate</em>,
        the second argument of <code>VarSpeedServo::write</code>{' '}
        <a href="#r3">[3]</a>. The third argument, <code>false</code>, makes the
        call non-blocking, so each command supersedes the last:
      </p>

      <Equation tag="2" math={EQ_CONTROL} />

      <p>
        Because the command is re-issued every frame the head never actually
        reaches the end stop. As the face approaches centre the error shrinks,
        the commanded speed shrinks with it, and inside the dead band the
        firmware freezes the servo at its current position. The net behaviour is
        proportional control in the velocity domain, implemented without a single
        floating-point multiply on the microcontroller.
      </p>
      <p>
        It is a better fit for this actuator than the obvious alternative. A
        hobby servo already closes its own position loop internally at a rate and
        with a gain the user cannot see; cascading a PID onto it means tuning an
        outer loop against unknown inner dynamics, and the integral term would
        wind up against the servo&rsquo;s own following error during every large
        slew. Commanding velocity instead sidesteps both problems, at the cost of
        giving up any notion of where the neck is pointing in absolute terms,
        which this system never needs, since the only reference is the face.
      </p>
      <p>The dead band of &plusmn;2 counts corresponds to</p>

      <Equation tag="3" math={EQ_DEADBAND} />

      <p>
        tight enough to look attentive and wide enough to stop the neck dithering
        on detector jitter. It is also, usefully, wider than the frame-to-frame
        wobble of a Haar bounding box on a stationary subject, which is the
        quantity that would otherwise drive the servo continuously.
      </p>

      <h3>3.4 Loss of target and re-centring</h3>
      <p>
        When no face is detected the host sends the frame <code>o!</code>. The
        firmware counts these; after 100 consecutive misses it drives the neck
        back to 90&deg; at a slow speed of 20 and resets the counter. Until then
        it holds position, so a momentary dropout does not move the head at all.
      </p>
      <p>
        The counter is expressed in frames rather than in seconds, and the
        capture loop calls <code>cap.read()</code> twice per iteration, and the
        first result is discarded, which keeps the driver&rsquo;s buffer from
        accumulating stale frames but halves the effective loop rate. At a
        30&nbsp;fps camera that gives roughly 15 iterations per second, so
      </p>

      <Equation tag="4" math={EQ_RECENTRE} />

      <p>
        which is a plausible &ldquo;the person has actually left&rdquo;
        threshold. But it is a function of camera frame rate, not of time: fit a
        60&nbsp;fps camera and the head starts giving up after 3.3&nbsp;s, with
        nothing in the code changed. Expressing the timeout in milliseconds
        inside the firmware, where <code>millis()</code> is already available,
        would make the behaviour invariant to the sensor.
      </p>

      <h2 id="s4">4. Interaction: the conversational loop</h2>

      <h3>4.1 Turn structure</h3>
      <p>
        <code>conversation.py</code> is an infinite loop that spawns two
        subprocesses per turn and waits for each in sequence: first{' '}
        <code>speech_to_text.py</code> (C0 + C1), then{' '}
        <code>text_to_speech_chatgpt.py</code> (C2 + C3). The two halves
        communicate through a file: the transcription stage writes{' '}
        <code>answer.txt</code>, the dialogue stage reads it.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Stages of one conversational turn.</caption>
          <thead>
            <tr>
              <th scope="col">Stage</th>
              <th scope="col">Input</th>
              <th scope="col">Output</th>
              <th scope="col">Provider</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>C0 Capture</td><td>Microphone</td><td><code>audio.wav</code></td><td>Local (PyAudio)</td></tr>
            <tr><td>C1 Transcribe</td><td><code>audio.wav</code></td><td><code>answer.txt</code></td><td>OpenAI Whisper <a href="#r4">[4]</a></td></tr>
            <tr><td>C2 Dialogue</td><td><code>answer.txt</code></td><td>Reply text</td><td>OpenAI chat completion <a href="#r5">[5]</a></td></tr>
            <tr><td>C3 Synthesise</td><td>Reply text</td><td><code>audio.mp3</code> &rarr; speaker</td><td>Google TTS <a href="#r7">[7]</a></td></tr>
          </tbody>
        </table>
      </div>

      <p>
        File-based hand-off is the simplest IPC available and it survives a crash
        in either half, which a pipe would not. Its weakness is that it carries
        no freshness marker: if C1 fails, <code>answer.txt</code> still holds the{' '}
        <em>previous</em> turn&rsquo;s transcript, and C2 will answer that
        question a second time with no indication anything went wrong. A turn
        counter written alongside the text, or a delete-after-read, closes the
        gap (<a href="#f-14">F14</a>).
      </p>

      <h3>4.2 Capture gating</h3>
      <p>
        <code>Recording</code> reads 1024-sample chunks and compares the mean
        absolute amplitude of each against a threshold to decide when to start
        and stop:
      </p>

      <pre><code>{`audio_np = np.frombuffer(data, dtype=np.int16)
amplitude = np.abs(audio_np).mean()

if amplitude > self.threshold and not self.recording_started:
    self.recording_started = True`}</code></pre>

      <p>
        The intent is a voice-activity detector. The implementation is not one,
        because of a units mismatch. <code>threshold</code> defaults to{' '}
        <code>0.01</code>, a value that reads as a normalised amplitude, while{' '}
        <code>amplitude</code> is computed over raw <code>int16</code> samples
        and therefore lives on a 0&ndash;32767 scale. Room tone clears 0.01 by
        two or three orders of magnitude. The start condition is true on the
        first chunk, the stop condition{' '}
        <code>amplitude &lt; threshold</code> is effectively never true, and
        capture always runs to the <code>max_duration</code> cap, reached at
      </p>

      <Equation tag="5" math={EQ_CHUNKS} />

      <p>
        So every utterance is a fixed six-second window: the robot waits out the
        full six seconds whether the speaker said one word or twenty, and cuts
        off anyone who needed a seventh. The fix is one line, either dividing by
        32768 before taking the mean or setting the threshold in{' '}
        <code>int16</code> units at a noise-floor-relative value in the low
        hundreds, and it converts a
        fixed window into genuine open-microphone capture
        (<a href="#f-1">F1</a>).
      </p>

      <h3>4.3 Dialogue</h3>
      <p>
        C2 appends the transcript to a message list, calls the chat completion
        endpoint (<code>gpt-3.5-turbo</code>) with <code>max_tokens=2000</code>{' '}
        and <code>temperature=0.2</code>, prints the reply and appends it back. A
        sliding window trims the history whenever it reaches four entries,
        dropping the two oldest and keeping, at most, the previous user/assistant
        exchange.
      </p>
      <p>
        That window is never exercised. <code>conversation.py</code> starts a{' '}
        <em>new interpreter</em> for C2 on every turn, so the message list is
        reconstructed empty each time and the model receives exactly one user
        message with no prior context. The robot answers each utterance in
        isolation: ask it a follow-up question and it has no idea what you are
        referring to. Persisting the history means either keeping C2 resident
        across turns or serialising the message list alongside{' '}
        <code>answer.txt</code>; the second is the smaller change and fits the
        existing file-based hand-off (<a href="#f-7">F7</a>).
      </p>
      <p>
        The low temperature is a sensible choice for a demonstrator rather than a
        timid one: it favours short, predictable answers, which bounds synthesis
        time and therefore bounds how long the jaw flaps for.
      </p>

      <h3>4.4 Synthesis and playback</h3>
      <p>
        The reply is synthesised with gTTS <a href="#r7">[7]</a> to{' '}
        <code>audio.mp3</code> and played with <code>playsound</code>{' '}
        <a href="#r8">[8]</a>. The code creates a thread for each step and then
        does:
      </p>

      <pre><code>{`t1.start()
t1.join()

t2.start()
t2.join()`}</code></pre>

      <p>
        Joining each thread immediately after starting it serialises them, so the
        threading buys nothing over two direct calls. It could not easily buy
        anything either: playback genuinely depends on synthesis having finished,
        because both operate on the same file path, and gTTS returns a complete
        file rather than a stream. The honest simplification is to drop the
        threads (<a href="#f-11">F11</a>); the honest improvement is a streaming
        synthesiser, which would let playback, and with it the jaw, start on the
        first audio chunk instead of after the last.
      </p>

      <h3>4.5 Latency budget</h3>
      <p>
        The stages are strictly sequential, so turn latency is their sum. The
        figures below are <em>estimates derived from the code structure</em> and
        from typical service round-trips, not measurements: no instrumented run
        is committed to the repository, and the hardware was not available while
        this was written.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>
            Estimated contributions to end-to-end turn latency. Order of
            magnitude only; see the colophon.
          </caption>
          <thead>
            <tr>
              <th scope="col">Contribution</th>
              <th scope="col">Estimate</th>
              <th scope="col">Driver</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Interpreter start-up &times;&nbsp;2</td><td className="num">1&ndash;3 s</td>
              <td>Fresh process per half-turn; imports <code>numpy</code>, <code>pyaudio</code>, <code>openai</code>, <code>librosa</code></td>
            </tr>
            <tr>
              <td>C0 capture</td><td className="num">6.0 s</td>
              <td>Fixed, per §4.2; the single largest item</td>
            </tr>
            <tr>
              <td>C1 Whisper round-trip</td><td className="num">1&ndash;2 s</td>
              <td>Upload of a 6&nbsp;s WAV plus inference</td>
            </tr>
            <tr>
              <td>C2 chat completion</td><td className="num">1&ndash;3 s</td>
              <td>Full response awaited; no streaming</td>
            </tr>
            <tr>
              <td>C3 synthesis</td><td className="num">0.5&ndash;1 s</td>
              <td>gTTS round-trip</td>
            </tr>
            <tr>
              <td>C3 playback</td><td className="num">= reply</td>
              <td>Blocking, for the duration of the spoken answer</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The ordering is the point. The capture window dominates every network
        stage, and it is the one item that costs nothing to fix. Repairing{' '}
        <a href="#f-1">F1</a> so that capture ends when the speaker stops would
        typically remove three to five seconds from a turn, more than moving
        transcription on-device, more than streaming the completion, and for a
        one-line change. Latency work that starts anywhere else on this table is
        optimising the wrong term.
      </p>

      <h2 id="s5">5. Jaw actuation from the audio envelope</h2>

      <h3>5.1 Envelope and mapping</h3>
      <p>
        <code>AudioSerialAnalyzer</code> loads the synthesised MP3 with librosa{' '}
        <a href="#r9">[9]</a>, normalises it, and maps it onto the
        servo&rsquo;s travel:
      </p>

      <pre><code>{`signal = signal / np.max(np.abs(signal))
signal = signal ** 2
signal = np.sqrt(signal)
signal_2 = 130 * signal + 50`}</code></pre>

      <p>
        Squaring and then taking the square root of a real signal is the identity{' '}
        <math><mrow><msqrt><msup><mi>s</mi><mn>2</mn></msup></msqrt><mo>=</mo><mrow><mo>|</mo><mi>s</mi><mo>|</mo></mrow></mrow></math>,
        so those two lines together are a full-wave rectifier written the long
        way. Writing{' '}
        <math><mrow><mover><mi>s</mi><mo>&#x7E;</mo></mover></mrow></math>{' '}
        for the peak-normalised signal, the whole block is
      </p>

      <Equation tag="6" math={EQ_ENVELOPE} />

      <p>
        The constants are not arbitrary. They map the normalised rectified sample
        onto [50&deg;, 180&deg;], which is exactly the jaw travel the firmware
        uses: 50&deg; is the closed position it homes to at boot and 180&deg; the
        fully open one. The affine mapping is the right shape, and the range
        agreement between the two files is deliberate.
      </p>
      <p>
        What is missing is the low-pass stage that makes an <em>envelope</em> an
        envelope. Equation (6) rectifies but does not smooth, so{' '}
        <math><mrow><mi>a</mi><mo stretchy="false">[</mo><mi>n</mi><mo stretchy="false">]</mo></mrow></math>{' '}
        still carries the full audio bandwidth: consecutive samples swing across
        the whole range at speech-carrier frequency rather than at syllable rate.
        A jaw cannot follow that. Neither, as it turns out, can the wire.
      </p>

      <h3>5.2 Bandwidth budget</h3>
      <p>
        This is where the design breaks, and the arithmetic is worth doing
        explicitly because the failure is quantitative rather than logical: the
        code is correct and simply asks for something the physical layer cannot
        deliver.
      </p>
      <p>
        <code>librosa.load</code> resamples to 22050&nbsp;Hz by default, so{' '}
        <math><mi>T</mi></math> seconds of audio yield{' '}
        <math><mrow><mn>22050</mn><mi>T</mi></mrow></math> float samples. They
        are serialised as a comma-joined decimal string, with{' '}
        <math><mrow><mi>b</mi><mo>&#x2248;</mo><mn>19</mn></mrow></math>{' '}
        bytes per value including the separator, for a <code>float64</code>{' '}
        repr. The byte rate the transmitter must sustain is
      </p>

      <Equation tag="7" math={EQ_RATE} />

      <p>
        The link is opened at 9600&nbsp;baud, 8N1, which carries ten bits per
        byte and therefore 960&nbsp;B/s. The ratio is the slowdown factor:
      </p>

      <Equation tag="8" math={EQ_SLOWDOWN} />

      <p>
        One second of speech takes roughly seven minutes to transmit. Even at the
        128000&nbsp;baud the jaw firmware actually opens (the two ends disagree,
        which is <a href="#f-2">F2</a>), the stream still runs 33 times slower
        than real time. Sample-rate streaming of a servo command over a UART is
        not a tuning problem that a faster baud rate would solve; it is a
        category error. Servo commands belong at a <em>control</em> rate, not at
        an audio rate.
      </p>
      <p>
        The fix follows straight from the numbers. Decimate the rectified signal
        to a frame rate the mechanism can actually track (50&nbsp;Hz is generous
        for a jaw, whose mechanical bandwidth is a few hertz), take the per-frame
        mean or peak, and quantise to one <code>uint8</code> spanning the same
        [50&deg;, 180&deg;] travel:
      </p>

      <Equation tag="9" math={EQ_DECIMATE} />

      <p>
        That is a factor of 8400 reduction. It leaves the link almost entirely
        idle, it removes the need for the binary-flag protocol the firmware
        currently implements, and, because averaging over a 20&nbsp;ms frame{' '}
        <em>is</em> the missing low-pass stage of §5.1, it makes the jaw track
        syllables rather than carrier oscillations. One change fixes the signal
        processing and the bandwidth problem together (<a href="#f-3">F3</a>).
      </p>

      <h3>5.3 What the firmware does instead</h3>
      <p>
        Given that constraint, <code>inmoov_jaw.ino</code> takes the pragmatic
        route: it accepts a single bit. On receiving <code>1!</code> it loops the
        jaw between 50&deg; and 180&deg; at near-maximum speed, re-reading the
        port on each cycle so that a subsequent <code>0!</code> breaks the loop
        and returns the jaw to closed. The mouth flaps while audio plays and
        shuts when it stops.
      </p>
      <p>
        It is a puppet, not a lip-sync, and it is an honest match to the
        bandwidth actually available. It is also the reason
        &ldquo;coordinate audio and mouth movement&rdquo; is still open on the
        project&rsquo;s own task list: that item is not blocked on animation
        work, it is blocked on §5.2.
      </p>

      <h2 id="s6">6. Host&ndash;microcontroller protocol</h2>
      <p>
        Both links carry ASCII frames terminated by <code>!</code>, parsed on the
        Arduino with <code>Serial.readStringUntil(&apos;!&apos;)</code> followed
        by <code>String::toInt()</code>. The neck protocol additionally uses a
        trailing letter as a frame-type tag, tested with{' '}
        <code>endsWith()</code> before the numeric conversion.
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Frame formats.</caption>
          <thead>
            <tr>
              <th scope="col">Frame</th>
              <th scope="col">Link</th>
              <th scope="col">Meaning</th>
              <th scope="col">Firmware action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>&lt;e&gt;x!</code></td><td>Neck</td>
              <td>Face present, yaw error <math><msub><mi>e</mi><mi>x</mi></msub></math> &isin; [&minus;30, 30]</td>
              <td>Slew toward an end stop at speed |<math><msub><mi>e</mi><mi>x</mi></msub></math>|; hold if |<math><msub><mi>e</mi><mi>x</mi></msub></math>| &le; 2</td>
            </tr>
            <tr>
              <td><code>o!</code></td><td>Neck</td>
              <td>No face this frame</td>
              <td>Hold; re-centre to 90&deg; after 100 consecutive</td>
            </tr>
            <tr>
              <td><code>1!</code></td><td>Jaw</td>
              <td>Audio playing</td>
              <td>Oscillate 50&deg; &harr; 180&deg; until a <code>0!</code> arrives</td>
            </tr>
            <tr>
              <td><code>0!</code></td><td>Jaw</td>
              <td>Silence</td>
              <td>Return to 50&deg; (closed)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        The error is formatted from a Python float, so a neck frame looks like{' '}
        <code>-12.345678x!</code>. <code>toInt()</code> stops at the first
        non-digit, truncating the fractional part. That is harmless here, since
        sub-count precision on a speed argument is meaningless, but it is
        load-bearing in a way that is easy to miss: the parse depends on the
        decimal point being the first non-digit character, so widening the value
        range or switching to exponential formatting would break it silently.
      </p>

      <h3>6.1 Link configuration mismatches</h3>
      <p>
        The baud rates at the two ends of each link do not agree in the committed
        tree:
      </p>

      <div className="scroll-wrapper">
        <table>
          <caption>Baud rate configured at each end of each serial link.</caption>
          <thead>
            <tr>
              <th scope="col">Link</th>
              <th scope="col">Host</th>
              <th scope="col">Firmware</th>
              <th scope="col">Agrees?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Neck</td>
              <td>250000, <code>face_detector.py</code></td>
              <td>115200, <code>face_detector.ino</code></td>
              <td>No</td>
            </tr>
            <tr>
              <td>Jaw</td>
              <td>9600, <code>AudioSerialAnalyzer.py</code></td>
              <td>128000, <code>inmoov_jaw.ino</code></td>
              <td>No</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        A UART running at mismatched rates does not fail cleanly. It delivers
        bytes, just the wrong ones, so the symptom is not silence but a garbled
        speed argument and erratic motion, which reads as a control-tuning
        problem rather than as a configuration one. The port names are hard-coded
        alongside them (<code>COM17</code> for the neck, <code>COM5</code> for
        the jaw), so all four values must be edited by hand before a first run on
        any machine. Promoting them to a single configuration file, read by the
        host and used to generate a firmware header, is the structural fix
        (<a href="#f-2">F2</a>).
      </p>

      <h2 id="s7">7. Demonstration</h2>
      <p>
        The project&rsquo;s pitch and tutorial videos are published as a YouTube
        playlist; the embed below plays it in order.
      </p>

      <figure>
        <div className="video-frame">
          <iframe
            src={EMBED}
            title="ChatMoov: project pitch and tutorial playlist"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </div>
        <figcaption>
          Pitch and tutorial playlist: the head tracking a face, and a full
          spoken exchange with the jaw animation running. Direct link:{' '}
          <a href={VIDEO}>the ChatMoov playlist on YouTube</a>.
        </figcaption>
      </figure>

      <h2 id="s8">8. Source-level review</h2>
      <p>
        The findings below come from reading the committed source rather than
        from running the hardware, which needs an INMOOV head, two Arduinos and
        an OpenAI key to exercise. Each is stated with the observation that
        supports it and the smallest change that would resolve it. Severity is
        relative to the project&rsquo;s own goal of being a reproducible
        demonstrator, so a defect that stops someone else getting the system
        running outranks one that merely makes it behave oddly.
      </p>

      <div className="scroll-wrapper">
        <table className="col-findings">
          <thead>
            <tr>
              <th>#</th>
              <th>Finding</th>
              <th>Where</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td id="f-1">F1</td>
              <td>
                The voice-activity threshold (<code>0.01</code>) is a normalised
                value compared against raw <code>int16</code> amplitudes on a
                0&ndash;32767 scale, so the gate is always open and the stop
                condition never fires. Every utterance becomes a fixed six-second
                window (§4.2), the largest single term in the latency budget.
              </td>
              <td><code>speech_to_text.py</code></td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-2">F2</td>
              <td>
                Host and firmware baud rates disagree on <em>both</em> serial
                links: 250000 against 115200, and 9600 against 128000 (§6.1).
                Port names are hard-coded to <code>COM17</code> and{' '}
                <code>COM5</code>. The symptom is corrupted bytes and erratic
                motion, not a clean failure.
              </td>
              <td>Both links</td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-3">F3</td>
              <td>
                One decimal value is streamed per audio sample; §5.2 puts this at
                roughly 436&times; real time on the configured link and 33&times;
                even at the firmware&rsquo;s rate. Decimating the envelope to
                ~50&nbsp;Hz and sending one <code>uint8</code> per frame fixes
                the bandwidth and the missing low-pass stage together.
              </td>
              <td><code>AudioSerialAnalyzer.py</code></td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-4">F4</td>
              <td>
                The final statement calls <code>time.sleep(0.1)</code>, but{' '}
                <code>time</code> was rebound earlier in the same function to a
                NumPy array of timestamps, so the call raises{' '}
                <code>AttributeError</code> on every run. Rename the array and
                import the module.
              </td>
              <td><code>AudioSerialAnalyzer.py</code></td>
              <td>High</td>
            </tr>
            <tr>
              <td id="f-5">F5</td>
              <td>
                <code>Recording.run()</code> constructs a second{' '}
                <code>Recording</code> internally and operates on that, so the
                outer instance&rsquo;s PyAudio stream is opened at construction
                and never closed, giving two input streams per turn, one of them leaked.
              </td>
              <td><code>speech_to_text.py</code></td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-6">F6</td>
              <td>
                The interpreter path <code>env_inmoov/Scripts/python.exe</code>{' '}
                is the Windows venv layout; POSIX uses{' '}
                <code>env_inmoov/bin/python</code>. <code>API/api.py</code>{' '}
                likewise builds paths with backslashes. Use{' '}
                <code>sys.executable</code> and <code>pathlib</code>.
              </td>
              <td><code>conversation.py</code>, <code>API/api.py</code></td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-7">F7</td>
              <td>
                Re-launching the interpreter per turn discards the chat history,
                so the sliding window in C2 never holds context and the robot
                cannot answer a follow-up question (§4.3).
              </td>
              <td><code>conversation.py</code></td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-8">F8</td>
              <td>
                <code>.gitignore</code> excludes <code>*.txt</code> to keep the
                API key out of history; the same rule also excludes the{' '}
                <code>requirements.txt</code> the README tells users to install
                from, and it is absent from the tree. Narrow the rule to{' '}
                <code>API/*.txt</code>.
              </td>
              <td><code>.gitignore</code></td>
              <td>Medium</td>
            </tr>
            <tr>
              <td id="f-9">F9</td>
              <td>
                The README asks the user to create <code>mpd.txt</code>; the code
                opens <code>mdp.txt</code>. A first-run failure with a
                file-not-found on a name that looks right.
              </td>
              <td>README, <code>API/api.py</code></td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-10">F10</td>
              <td>
                Module-level <code>GPT_Speech_to_Text()</code> and{' '}
                <code>ChatGPT_Text_to_Speech()</code> instances are constructed,
                opening an audio stream and reading the API key, then never
                used, because the work is done by subprocesses.
              </td>
              <td><code>conversation.py</code></td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-11">F11</td>
              <td>
                A second, unused <code>AudioSerialAnalyzer</code> is defined
                inline and references <code>serial</code> and <code>np</code>,
                neither of which the module imports. Separately, the two
                playback threads are joined immediately after starting, so they
                run serially (§4.4).
              </td>
              <td><code>text_to_speech_chatgpt.py</code></td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-12">F12</td>
              <td>
                The <code>docker run</code> invocation continues lines with{' '}
                <code>^</code> (Windows <code>cmd</code>) inside a{' '}
                <code>#!/usr/bin/env bash</code> script, so the command is parsed
                as several broken ones.
              </td>
              <td><code>Docker/run.sh</code></td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-13">F13</td>
              <td>
                Only <code>faces[0]</code> is tracked, with no association
                between frames, so the target can swap between people. The
                vertical error is computed and discarded (§3.2).
              </td>
              <td><code>face_detector.py</code></td>
              <td>Low</td>
            </tr>
            <tr>
              <td id="f-14">F14</td>
              <td>
                The <code>answer.txt</code> hand-off has no freshness marker, so
                a failed C1 makes C2 answer the previous turn again (§4.1). The
                TTS language is <code>fr</code> in one module and{' '}
                <code>ca</code> in another.
              </td>
              <td>C1/C2 hand-off</td>
              <td>Low</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="s9">9. What the design gets right</h2>
      <p>
        A defect list is not an assessment, and it would be a poor reading of
        this project to take §8 as one. Four decisions are worth keeping, and
        three of them are the kind that are easy to get wrong.
      </p>
      <ul>
        <li>
          <strong>Process isolation between P0 and P1.</strong> The single
          hardest constraint in an interactive robot is that perception must not
          stall behind network I/O. Separate OS processes solve it completely and
          leave no shared-state bugs to debug, whereas a threaded design with
          a shared serial handle would have needed locking that nothing here
          would have justified.
        </li>
        <li>
          <strong>Speed-proportional bang-bang control.</strong> §3.3&rsquo;s law
          extracts proportional behaviour from an actuator that only accepts
          positions, using integer arithmetic and no tuning. Cascading a PID onto
          the servo&rsquo;s own internal loop would have been the obvious move
          and the worse one.
        </li>
        <li>
          <strong>Explicit failure edges.</strong> Every network stage validates
          its output before passing it on, so failures surface where they occur
          instead of turning into null servo commands. This is visible in the
          control-flow diagram as a design intention, not retrofitted.
        </li>
        <li>
          <strong>An on-device transcription path already scaffolded.</strong>{' '}
          The <code>Docker/</code> directory builds whisper.cpp{' '}
          <a href="#r6">[6]</a> with a downloadable model. The migration off
          cloud STT, the largest latency item after <a href="#f-1">F1</a>, has
          its groundwork laid rather than merely listed.
        </li>
      </ul>
      <p>
        The project&rsquo;s own task list keeps three items open: coordinating
        audio with jaw motion, running text-to-speech locally, and collapsing
        onto a single Arduino. The analysis above reorders them by leverage. The
        first is blocked on <a href="#f-3">F3</a> and is really a signal-processing
        task, not an animation one; the third subsumes <a href="#f-2">F2</a> by
        removing one of the two link configurations entirely; the second is worth
        less than fixing <a href="#f-1">F1</a>, which costs one line.
      </p>

      <h2 id="s10">10. Reproducing the system</h2>
      <ol>
        <li>
          <strong>Create the environment.</strong>
          <pre><code>{`python3 -m venv env_inmoov
source env_inmoov/bin/activate`}</code></pre>
          Dependencies are <code>opencv-python</code>, <code>numpy</code>,{' '}
          <code>pyserial</code>, <code>pyaudio</code>, <code>openai</code>,{' '}
          <code>gtts</code>, <code>playsound</code> and <code>librosa</code>; no{' '}
          <code>requirements.txt</code> is committed
          (<a href="#f-8">F8</a>).
        </li>
        <li>
          <strong>Provide OpenAI credentials.</strong> <code>API/api.py</code>{' '}
          reads the key from <code>API/mdp.txt</code> and the organisation from{' '}
          <code>API/organisation.txt</code>, one value per file, no trailing
          whitespace. Note that the README asks for a differently-spelled filename
          (<a href="#f-9">F9</a>).
        </li>
        <li>
          <strong>Flash the firmware.</strong> <code>face_detector.ino</code> to
          the neck board and <code>inmoov_jaw.ino</code> to the jaw board, each
          with the servo on pin&nbsp;9. Both need <code>VarSpeedServo</code>{' '}
          <a href="#r3">[3]</a>.
        </li>
        <li>
          <strong>Reconcile the links.</strong> Set the port names in{' '}
          <code>face_detector.py</code> and <code>AudioSerialAnalyzer.py</code>{' '}
          to your machine&rsquo;s devices, and make each baud rate match its
          firmware&rsquo;s <code>Serial.begin()</code>; they do not agree as
          committed (<a href="#f-2">F2</a>).
        </li>
        <li>
          <strong>Run.</strong> <code>python3 inmoov_v1.py</code> starts both
          subsystems; running <code>face_detector.py</code> and{' '}
          <code>conversation.py</code> separately isolates one of them for
          debugging. Ctrl-C terminates the children before the parent.
        </li>
      </ol>

      <h2 id="references">References</h2>
      <ol>
        <li id="r1">
          <em>INMOOV, Head v3 build documentation</em>.{' '}
          <a href={INMOOV}>inmoov.fr/head-3</a>
        </li>
        <li id="r2">
          P. Viola and M. Jones, <em>Rapid Object Detection using a Boosted
          Cascade of Simple Features</em>. CVPR 2001. Implemented as OpenCV&rsquo;s{' '}
          <code>CascadeClassifier</code>.
        </li>
        <li id="r3">
          <em>VarSpeedServo</em>, an Arduino servo library with per-move speed
          control.{' '}
          <a href="https://github.com/netlabtoolkit/VarSpeedServo">
            github.com/netlabtoolkit/VarSpeedServo
          </a>
        </li>
        <li id="r4">
          OpenAI, <em>Whisper</em>.{' '}
          <a href="https://openai.com/research/whisper">openai.com/research/whisper</a>
        </li>
        <li id="r5">
          OpenAI, <em>Chat Completions API reference</em>.{' '}
          <a href="https://platform.openai.com/docs/api-reference/chat/create">
            platform.openai.com
          </a>
        </li>
        <li id="r6">
          G. Gerganov, <em>whisper.cpp</em>, the model the project&rsquo;s
          Docker image builds.{' '}
          <a href="https://github.com/ggerganov/whisper.cpp">
            github.com/ggerganov/whisper.cpp
          </a>
        </li>
        <li id="r7">
          <em>gTTS</em>, a Python interface to Google Translate&rsquo;s
          text-to-speech API.{' '}
          <a href="https://github.com/pndurette/gTTS">github.com/pndurette/gTTS</a>
        </li>
        <li id="r8">
          <em>playsound</em> 1.2.2.{' '}
          <a href="https://pypi.org/project/playsound/1.2.2/">pypi.org/project/playsound</a>
        </li>
        <li id="r9">
          <em>librosa</em>, audio analysis for Python.{' '}
          <a href="https://librosa.org/">librosa.org</a>
        </li>
        <li id="r10">
          Project source.{' '}
          <a href={REPO}>github.com/TheoTime01/ChatMoov</a>
        </li>
        <li id="r11">
          The Assembly, <em>Build a Face-Tracking Camera with Computer
          Vision</em>, the reference the tracking loop is adapted from.{' '}
          <a href="https://github.com/The-Assembly/Build-A-Face-Tracking-Camera-With-Computer-Vision">
            github.com/The-Assembly
          </a>
        </li>
      </ol>

      <p className="colophon">
        Every parameter quoted above is read from the source and configuration
        files in the project repository <a href="#r10">[10]</a> rather than
        re-derived: detector parameters, thresholds and serial settings from the
        Python modules, servo angles and baud rates from the two Arduino
        sketches, and the process structure from the diagrams committed with
        them. Derived quantities are marked as derived. The timings in §3.4 and
        §4.5 and the byte rates in §5.2 are analytical estimates from that
        source, not measurements: no instrumented run is committed, and the
        robot was not available while this was written. Typeset with{' '}
        <a href="https://github.com/vincentdoerig/latex-css">LaTeX.css</a>.
      </p>
    </article>
  );
};

export default ChatMoovProject;
